/**
 * The project's hierarchy tree, shared through context (`KLQ-20260820-005` §3).
 *
 * **One fetch per project session, made structural rather than incidental.** The tree is a
 * single cache entry — `hierarchyQueryOptions` is keyed by the project alone and now
 * `staleTime: Infinity`, so nothing re-fetches it while a session is open. But a cached query
 * is still a *query*: the "fetched at most once" guarantee was real only because every caller
 * happened to hit the same key. Holding the result in a provider is what makes the guarantee
 * the shape of the code — a screen that wants the tree asks the provider, and a screen that
 * reaches for it without one is a wiring mistake that throws rather than silently fetching.
 *
 * **The whole query result is handed down, not a copy of its data.** Callers keep
 * `isPending` / `isError` / `data` exactly as react-query reports them, and nothing mirrors
 * server state into a store (frontend rule 3). The provider owns the one `useQuery`; a
 * consumer never issues its own.
 *
 * Mounted in `ProjectShell`, so every project-scoped screen is under it — which is what makes
 * "once per project session" true. A project that does not load has no tree to ask for, so the
 * shell's missing-project early return does not mount one.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { hierarchyQueryOptions } from './queries';
import type { HierarchyTree } from './api';

/** The full query result for the project's hierarchy — data, status, error, all of it. */
export type ProjectHierarchy = UseQueryResult<HierarchyTree>;

const HierarchyContext = createContext<ProjectHierarchy | null>(null);

export interface HierarchyProviderProps {
  /** The project whose tree is held. One provider per project session. */
  readonly projectId: string;
  readonly children: ReactNode;
}

export function HierarchyProvider({ projectId, children }: HierarchyProviderProps) {
  const query = useQuery(hierarchyQueryOptions(projectId));
  return <HierarchyContext.Provider value={query}>{children}</HierarchyContext.Provider>;
}

/**
 * The project's hierarchy tree, from the provider above.
 *
 * Throws outside a provider, like `useContextMenu`: a screen that reaches for the tree without
 * a provider is a wiring mistake, and a silent fetch would hide it.
 */
export function useProjectHierarchy(): ProjectHierarchy {
  const value = useContext(HierarchyContext);
  if (value === null) {
    throw new Error('useProjectHierarchy must be used within a <HierarchyProvider>');
  }
  return value;
}
